import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import type { JobListItem } from "@meridian-mdm/shared";
import { bucketCounts } from "@meridian-mdm/shared";
import { fetchJobs } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ListChecks, ChevronsUpDown, ChevronUp, ChevronDown } from "lucide-react";

const columnHelper = createColumnHelper<JobListItem>();

const columns = [
  columnHelper.accessor("id", {
    header: "ID",
    cell: (info) => <span className="font-mono text-xs">#{info.getValue()}</span>,
  }),
  columnHelper.accessor("appName", {
    header: "App Name",
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),
  columnHelper.accessor("targetVersion", {
    header: "Target Version",
    cell: (info) => <span className="font-mono text-sm">{info.getValue()}</span>,
  }),
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const status = info.getValue();
      return (
        <Badge
          variant="outline"
          className={
            status === "running"
              ? "border-blue-300 bg-blue-100 text-blue-700"
              : "border-green-300 bg-green-100 text-green-700"
          }
        >
          {status === "running" ? "Running" : "Completed"}
        </Badge>
      );
    },
  }),
  columnHelper.accessor("startedAt", {
    header: "Started",
    cell: (info) => (
      <span className="text-sm text-muted-foreground">
        {new Date(info.getValue()).toLocaleString()}
      </span>
    ),
  }),
  columnHelper.accessor("completedAt", {
    header: "Completed",
    cell: (info) => {
      const val = info.getValue();
      return val ? (
        <span className="text-sm text-muted-foreground">{new Date(val).toLocaleString()}</span>
      ) : (
        <span className="text-sm text-muted-foreground">--</span>
      );
    },
  }),
  columnHelper.display({
    id: "progress",
    header: "Progress",
    enableSorting: false,
    cell: ({ row }) => {
      const job = row.original;
      const { succeeded, failed, timeout, skipped, total } = bucketCounts(
        job.summary,
        job.skippedDevices,
        job.totalDevices,
      );
      return (
        <span
          className="font-mono text-sm tabular-nums"
          title="succeeded / failed / timeout / skipped / total"
        >
          <span className="text-green-600">{succeeded}</span>
          <span className="text-muted-foreground"> / </span>
          <span className="text-red-600">{failed}</span>
          <span className="text-muted-foreground"> / </span>
          <span className="text-orange-500">{timeout}</span>
          <span className="text-muted-foreground"> / </span>
          <span className="text-gray-500">{skipped}</span>
          <span className="text-muted-foreground"> / </span>
          <span>{total}</span>
        </span>
      );
    },
  }),
];

export default function Jobs() {
  const navigate = useNavigate();
  const {
    data: jobs,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["jobs"],
    queryFn: fetchJobs,
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: jobs ?? [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Update Jobs</h1>
        <p className="text-muted-foreground">
          View and manage app update jobs across your device fleet.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load jobs: {error instanceof Error ? error.message : "Unknown error"}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : jobs && jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border py-16">
          <ListChecks className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium">No jobs yet</p>
          <p className="text-sm text-muted-foreground">
            Create an update job from the Dashboard to get started.
          </p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder ? null : canSort ? (
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {sorted === "asc" ? (
                              <ChevronUp className="h-3.5 w-3.5" />
                            ) : sorted === "desc" ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />
                            )}
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => void navigate(`/jobs/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
